import type { ReactElement, SVGProps } from 'react';

/* Line icon set for navigation and dashboard. 24px grid, 1.7px stroke, drawn to match
   the Zeriti chrome. Icons are decorative (aria-hidden); the text label carries meaning. */

export type IconName =
  | 'dashboard'
  | 'orders'
  | 'approvals'
  | 'customers'
  | 'purchase-orders'
  | 'manufacturers'
  | 'products'
  | 'inventory'
  | 'documents'
  | 'proformas'
  | 'payments'
  | 'company'
  | 'users'
  | 'roles'
  | 'policy'
  | 'numbering'
  | 'terms'
  | 'support'
  | 'audit'
  | 'subscription'
  | 'sessions'
  | 'globe'
  | 'tenants'
  | 'plans'
  | 'truck'
  | 'partners'
  | 'freight'
  | 'receivables'
  | 'reconcile'
  | 'alert'
  | 'bell'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'clock'
  | 'check'
  | 'reports'
  | 'chevron-right'
  | 'menu'
  | 'dot';

const PATHS: Record<IconName, ReactElement> = {
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.5" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
    </>
  ),
  orders: (
    <>
      <path d="M4 5h2l2.2 10.5a1.5 1.5 0 0 0 1.47 1.2h7.9a1.5 1.5 0 0 0 1.46-1.14L21 8H7" />
      <circle cx="10.5" cy="20" r="1.3" />
      <circle cx="17.5" cy="20" r="1.3" />
    </>
  ),
  approvals: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12.2 2.4 2.4 4.6-5" />
    </>
  ),
  customers: (
    <>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3.8 19.5c.6-3 2.8-4.7 5.2-4.7s4.6 1.7 5.2 4.7" />
      <path d="M15.5 5.8a3.2 3.2 0 0 1 0 5.4M17.6 15c1.5.7 2.5 2.2 2.9 4.1" />
    </>
  ),
  'purchase-orders': (
    <>
      <rect x="5" y="4.5" width="14" height="16" rx="2" />
      <path d="M9 4.5V3h6v1.5" />
      <path d="M9 10h6M9 13.5h6M9 17h3.5" />
    </>
  ),
  manufacturers: (
    <>
      <path d="M3.5 20.5V9l5.5 3.5V9l5.5 3.5V4.5h6v16Z" />
      <path d="M16.8 16.5h1.5" />
    </>
  ),
  products: (
    <>
      <path d="M12 3 20 7.2v9.6L12 21l-8-4.2V7.2Z" />
      <path d="M4.3 7.4 12 11.5l7.7-4.1M12 11.5V20.6" />
    </>
  ),
  inventory: (
    <>
      <path d="m12 3.5 8.5 4.3L12 12 3.5 7.8Z" />
      <path d="m3.5 12.2 8.5 4.2 8.5-4.2" />
      <path d="m3.5 16.4 8.5 4.2 8.5-4.2" />
    </>
  ),
  documents: (
    <>
      <path d="M6.5 3.5h7L18.5 8v12.5h-12Z" />
      <path d="M13 3.5V8h5" />
    </>
  ),
  proformas: (
    <>
      <path d="M6 3.5h12v17l-2.4-1.5-2.4 1.5-2.4-1.5L8.4 21 6 19.5Z" />
      <path d="M9.5 8.5h5M9.5 12h5" />
    </>
  ),
  payments: (
    <>
      <rect x="3" y="6.5" width="18" height="11" rx="2" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M6.3 9.8h.01M17.7 14.2h.01" />
    </>
  ),
  company: (
    <>
      <path d="M5 20.5V4.5h9v16M14 9.5h5v11M21 20.5H3" />
      <path d="M8 8h3M8 11.5h3M8 15h3" />
    </>
  ),
  users: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5" />
    </>
  ),
  roles: (
    <>
      <path d="M12 3.5 19 6v5.3c0 4.3-2.8 7.5-7 9.2-4.2-1.7-7-4.9-7-9.2V6Z" />
      <path d="m9.3 11.6 1.9 1.9 3.5-3.8" />
    </>
  ),
  policy: (
    <>
      <circle cx="6" cy="6" r="2.2" />
      <circle cx="18" cy="12" r="2.2" />
      <circle cx="6" cy="18" r="2.2" />
      <path d="M8.2 6H13a3 3 0 0 1 3 3v.8M15.8 12H11a3 3 0 0 0-3 3v.8" />
    </>
  ),
  numbering: (
    <>
      <path d="M9.5 4 8 20M16 4l-1.5 16M4.5 9h16M3.5 15h16" />
    </>
  ),
  terms: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
      <path d="m9.5 15.5 1.8 1.8 3.4-3.6" />
    </>
  ),
  support: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="m6 6 3.5 3.5M18 6l-3.5 3.5M18 18l-3.5-3.5M6 18l3.5-3.5" />
    </>
  ),
  audit: (
    <>
      <path d="M5 4.5h14v16H5Z" />
      <path d="M8.5 9h7M8.5 12.5h7M8.5 16h4" />
    </>
  ),
  subscription: (
    <>
      <path d="M4 20V10M10 20V4.5M16 20v-8M21 20H3" />
    </>
  ),
  sessions: (
    <>
      <rect x="3" y="5" width="18" height="12" rx="2" />
      <path d="M9 20.5h6M12 17v3.5" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.5 2.4 3.8 5.3 3.8 8.5s-1.3 6.1-3.8 8.5c-2.5-2.4-3.8-5.3-3.8-8.5s1.3-6.1 3.8-8.5Z" />
    </>
  ),
  tenants: (
    <>
      <path d="M3.5 20.5v-11l5-3.5 5 3.5v11M13.5 20.5h7v-8h-7" />
      <path d="M6.5 13h2M6.5 16.5h2M16.5 16h1.5" />
    </>
  ),
  plans: (
    <>
      <path d="M12 3.5 20 7.5l-8 4-8-4Z" />
      <path d="m4 12 8 4 8-4M4 16.5l8 4 8-4" />
    </>
  ),
  truck: (
    <>
      <path d="M2.5 6.5h11v9h-11z" />
      <path d="M13.5 9.5h4l3 3.2v2.8h-7" />
      <circle cx="7" cy="17.5" r="1.8" />
      <circle cx="17" cy="17.5" r="1.8" />
    </>
  ),
  partners: (
    <>
      <path d="M3.5 18.5v-9l8.5-5 8.5 5v9" />
      <path d="M8 18.5v-5h8v5" />
      <path d="M3.5 18.5h17" />
    </>
  ),
  freight: (
    <>
      <path d="M4 7.5 12 3.5l8 4v9l-8 4-8-4z" />
      <path d="M4 7.5l8 4 8-4M12 11.5v9" />
    </>
  ),
  receivables: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
      <path d="M3.5 9.5h17M7 14.5h4" />
      <path d="M15 13.5l1.5 1.5 2.5-3" />
    </>
  ),
  reconcile: (
    <>
      <path d="M4 8.5h13l-3-3M20 15.5H7l3 3" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 21 19.5H3z" />
      <path d="M12 10v4.5M12 17v.01" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 1.5h-15z" />
      <path d="M10 20.5h4" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="3.8" />
      <path d="M12 2.8v2M12 19.2v2M2.8 12h2M19.2 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M5.5 18.5l1.4-1.4M17.1 6.9l1.4-1.4" />
    </>
  ),
  moon: <path d="M19.5 14.5A7.5 7.5 0 0 1 9.5 4.5a7.5 7.5 0 1 0 10 10z" />,
  monitor: (
    <>
      <rect x="3" y="4.5" width="18" height="12" rx="1.5" />
      <path d="M9 20h6M12 16.5V20" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  reports: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M21 20H3" />
    </>
  ),
  'chevron-right': <path d="M9.5 6l6 6-6 6" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  dot: <circle cx="12" cy="12" r="2.5" />,
};

export function Icon({ name, size = 18, ...rest }: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name] ?? PATHS.dot}
    </svg>
  );
}

/** Zeriti product mark: three offset strata (batch layers) forming a Z, coloured from the theme tokens. */
export function ZeritiMark({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <rect width="32" height="32" rx="7" style={{ fill: 'var(--primary)' }} />
      <rect x="8" y="7.5" width="16" height="4" rx="2" style={{ fill: 'var(--primary-foreground)' }} />
      <rect x="7" y="14" width="18" height="4" rx="2" style={{ fill: 'var(--secondary)' }} transform="rotate(-33 16 16)" />
      <rect x="8" y="20.5" width="16" height="4" rx="2" style={{ fill: 'var(--primary-foreground)' }} />
    </svg>
  );
}

/** Full wordmark for the sidebar and auth screens. */
export function ZeritiWordmark({ markSize = 28 }: { markSize?: number }) {
  return (
    <>
      <ZeritiMark size={markSize} />
      <span className="wordmark">Zeriti</span>
    </>
  );
}
